import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useDeepLinkRuntimeState } from '../../../deepLink';
import { SIDE_PANEL_CLOSE_REASONS, SIDE_PANEL_CONTROL_COMMANDS } from '../../constants';
import type { SidePanelCommandResult, SidePanelSession } from '../../types';
import { SidePanelOwnerContext } from '../contexts/SidePanelOwnerContext';
import { reduceSidePanelCommand } from '../domainLogic/commandReducer';
import { resolveSidePanelDeepLinkPath, resolveSidePanelSubjectBasePath } from '../domainLogic/deepLinkPaths';
import { layoutAtom, sessionAtom, surfaceAtom } from '../state/atoms';
import { dispatchOwnerNotification } from '../state/dispatchOwnerNotification';
import { useInternalSidePanelSystem } from './useInternalSidePanelSystem';

type OpenSidePanelOptions = {
    subject?: SidePanelSession['subject'];
    payload?: Record<string, unknown>;
};

/**
 * Provides serialized commands to open, close, toggle, or update the active side-panel session.
 *
 * @remarks
 * Requires SidePanelSystemProvider and DeepLinkProvider. Opening resolves the nearest declaring owner,
 * falling back to the page surface, and uses the supplied subject or the surface subject.
 * Commands check declaration availability and required subjects, but do not enforce affordance permissions.
 * Successful transitions update session state, owner notifications, and applicable runtime deep-link state.
 * @returns Memoized open, close, toggle, setSubject, patchPayload, and setLayout functions returning result promises.
 */
export function useSidePanelCommands() {
    const { store, commandHandlers, controlQueue } = useInternalSidePanelSystem();
    const deepLinkRuntimeState = useDeepLinkRuntimeState();
    const ownerContext = useContext(SidePanelOwnerContext);
    const ownerContextRef = useRef(ownerContext);
    ownerContextRef.current = ownerContext;

    const openDirect = useCallback(
        async (panelId: string, options: OpenSidePanelOptions = {}): Promise<SidePanelCommandResult> => {
            const surface = store.get(surfaceAtom);
            const caller = ownerContextRef.current;
            if (!surface) return { status: 'rejected', reason: 'surfaceUnavailable' };

            const registration = caller?.ownerRegistrations.find((owner) =>
                owner.declarations.some(({ definition }) => definition.id === panelId)
            );
            const declaration = registration?.declarations.find(({ definition }) => definition.id === panelId);
            const pageDeclaration = surface.declarations.find(({ definition }) => definition.id === panelId);
            const resolvedDeclaration = declaration ?? pageDeclaration;
            if (!resolvedDeclaration) return { status: 'rejected', reason: 'notDeclared' };

            const subject = options.subject ?? surface.subject;
            if (resolvedDeclaration.definition.kind === 'entity' && !subject) {
                return { status: 'rejected', reason: 'subjectMissing' };
            }
            if (resolvedDeclaration.enabled && !resolvedDeclaration.enabled(subject ?? null)) {
                return { status: 'rejected', reason: 'disabled' };
            }

            const currentSession = store.get(sessionAtom);
            if (
                currentSession?.panelId === panelId &&
                currentSession.ownerId === (registration?.ownerId ?? surface.ownerId) &&
                isEqual(currentSession.subject, subject ?? null)
            ) {
                if (options.payload === undefined || isEqual(currentSession.payload, options.payload)) {
                    return { status: 'ignored', reason: 'alreadyOpen' };
                }
                const payloadTransition = reduceSidePanelCommand(
                    { session: currentSession, isExpanded: store.get(layoutAtom).isExpanded },
                    { type: SIDE_PANEL_CONTROL_COMMANDS.patchPayload, payload: options.payload }
                );
                store.set(sessionAtom, payloadTransition.state.session);
                const result: SidePanelCommandResult =
                    payloadTransition.result.status === 'ok'
                        ? { ...payloadTransition.result, sessionId: currentSession.sessionId }
                        : payloadTransition.result;
                return result;
            }

            const resolvedOwner = registration ?? {
                ownerId: surface.ownerId,
                declarations: surface.declarations,
                entitySecurityChain: surface.entitySecurityChain,
                featureSecurityChain: surface.featureSecurityChain,
                deepLinkBasePath: surface.deepLinkBasePath
            };
            const registrationIndex = registration
                ? (caller?.ownerRegistrations.findIndex(({ ownerId }) => ownerId === registration.ownerId) ?? -1)
                : -1;
            const ancestorChain =
                registrationIndex >= 0
                    ? caller!.ownerRegistrations.slice(registrationIndex).map(({ ownerId }) => ownerId)
                    : [surface.ownerId];
            const sessionBasePath =
                resolvedDeclaration.definition.deepLink?.mode === 'entityNode'
                    ? resolvedOwner.deepLinkBasePath
                    : resolveSidePanelSubjectBasePath(resolvedOwner.deepLinkBasePath, subject?.entityName);
            const deepLinkPath = resolveSidePanelDeepLinkPath(
                sessionBasePath,
                panelId,
                resolvedDeclaration.definition.deepLink
            );
            const session: SidePanelSession = {
                sessionId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                panelId,
                ownerId: resolvedOwner.ownerId,
                ownerChain: [
                    resolvedOwner.ownerId,
                    ...ancestorChain.filter((ownerId) => ownerId !== resolvedOwner.ownerId)
                ],
                subject: subject ?? null,
                payload: options.payload,
                entitySecurityChain: resolvedOwner.entitySecurityChain,
                featureSecurityChain: resolvedOwner.featureSecurityChain,
                deepLinkBasePath: resolvedOwner.deepLinkBasePath,
                deepLinkPath,
                deepLinkMode: resolvedDeclaration.definition.deepLink?.mode
            };
            const transition = reduceSidePanelCommand(
                { session: store.get(sessionAtom), isExpanded: store.get(layoutAtom).isExpanded },
                { type: SIDE_PANEL_CONTROL_COMMANDS.open, session }
            );

            if (transition.result.status === 'ok') {
                const previous = store.get(sessionAtom);
                if (previous) {
                    if (previous.deepLinkPath) deepLinkRuntimeState.clearDeepLinkRuntimeState(previous.deepLinkPath);
                    dispatchOwnerNotification(
                        previous,
                        'panel.closed',
                        { panelId: previous.panelId, reason: SIDE_PANEL_CLOSE_REASONS.replaced },
                        commandHandlers
                    );
                }
                store.set(sessionAtom, transition.state.session);
                store.set(layoutAtom, { isExpanded: transition.state.isExpanded });
                if (resolvedOwner.ownerId === surface.ownerId) {
                    surface.onVisiblePanelChange?.(panelId);
                }
                dispatchOwnerNotification(session, 'panel.opened', { panelId }, commandHandlers);
                if (deepLinkPath) {
                    if (subject && session.deepLinkMode !== 'entityNode') {
                        deepLinkRuntimeState.setDeepLinkRuntimeState(
                            sessionBasePath,
                            { id: subject.reference.id },
                            { merge: true }
                        );
                    }
                    const value =
                        session.deepLinkMode === 'entityNode' && subject ? { id: subject.reference.id } : true;
                    deepLinkRuntimeState.setDeepLinkRuntimeState(deepLinkPath, value, { merge: true });
                }
            }

            return {
                ...transition.result,
                ...(transition.result.status === 'ok' ? { sessionId: session.sessionId } : {})
            };
        },
        [commandHandlers, deepLinkRuntimeState, store]
    );

    const open = useCallback(
        (panelId: string, options: OpenSidePanelOptions = {}) => controlQueue.run(() => openDirect(panelId, options)),
        [controlQueue, openDirect]
    );

    const closeDirect = useCallback(async (): Promise<SidePanelCommandResult> => {
        const current = store.get(sessionAtom);
        if (current?.deepLinkPath) deepLinkRuntimeState.clearDeepLinkRuntimeState(current.deepLinkPath);
        const transition = reduceSidePanelCommand(
            { session: current, isExpanded: store.get(layoutAtom).isExpanded },
            { type: SIDE_PANEL_CONTROL_COMMANDS.close }
        );
        if (current && transition.result.status === 'ok') {
            dispatchOwnerNotification(
                current,
                'panel.closed',
                { panelId: current.panelId, reason: SIDE_PANEL_CLOSE_REASONS.command },
                commandHandlers
            );
        }
        store.set(sessionAtom, transition.state.session);
        store.set(layoutAtom, { isExpanded: transition.state.isExpanded });
        if (current?.ownerId === store.get(surfaceAtom)?.ownerId) {
            store.get(surfaceAtom)?.onVisiblePanelChange?.(undefined);
        }
        return transition.result;
    }, [commandHandlers, deepLinkRuntimeState, store]);

    const close = useCallback(() => controlQueue.run(closeDirect), [closeDirect, controlQueue]);

    const toggle = useCallback(
        (panelId: string, options: OpenSidePanelOptions = {}) =>
            controlQueue.run(async () => {
                const surface = store.get(surfaceAtom);
                const caller = ownerContextRef.current;
                if (!surface) return { status: 'rejected', reason: 'surfaceUnavailable' };
                const registration = caller?.ownerRegistrations.find((owner) =>
                    owner.declarations.some(({ definition }) => definition.id === panelId)
                );
                const current = store.get(sessionAtom);
                const subject = options.subject ?? surface.subject;
                if (
                    current?.panelId === panelId &&
                    current.ownerId === (registration?.ownerId ?? surface.ownerId) &&
                    isEqual(current.subject, subject ?? null)
                ) {
                    return closeDirect();
                }
                return openDirect(panelId, options);
            }),
        [closeDirect, controlQueue, openDirect, store]
    );

    const setSubject = useCallback(
        (subject: NonNullable<SidePanelSession['subject']>) =>
            controlQueue.run(async () => {
                const current = store.get(sessionAtom);
                const transition = reduceSidePanelCommand(
                    { session: current, isExpanded: store.get(layoutAtom).isExpanded },
                    { type: SIDE_PANEL_CONTROL_COMMANDS.setSubject, subject }
                );
                store.set(sessionAtom, transition.state.session);
                if (current?.deepLinkPath && current.deepLinkMode === 'entityNode') {
                    deepLinkRuntimeState.setDeepLinkRuntimeState(
                        current.deepLinkPath,
                        { id: subject.reference.id },
                        { merge: true }
                    );
                } else if (current?.deepLinkPath) {
                    const subjectBasePath = resolveSidePanelSubjectBasePath(
                        current.deepLinkBasePath,
                        subject.entityName
                    );
                    const nodeKey = current.deepLinkPath.split('.').at(-1);
                    const deepLinkPath = `${subjectBasePath}.${nodeKey}`;
                    const selectionChanged =
                        current.subject?.entityName !== subject.entityName ||
                        current.subject?.reference.id !== subject.reference.id;
                    if (selectionChanged) {
                        deepLinkRuntimeState.clearDeepLinkRuntimeState(current.deepLinkPath);
                    }
                    deepLinkRuntimeState.setDeepLinkRuntimeState(
                        subjectBasePath,
                        { id: subject.reference.id },
                        { merge: true }
                    );
                    if (selectionChanged) {
                        deepLinkRuntimeState.setDeepLinkRuntimeState(deepLinkPath, true, { merge: true });
                    }
                    store.set(sessionAtom, (session) => (session ? { ...session, deepLinkPath } : null));
                }
                return transition.result;
            }),
        [controlQueue, deepLinkRuntimeState, store]
    );

    const patchPayload = useCallback(
        (payload: Record<string, unknown>) =>
            controlQueue.run(async () => {
                const transition = reduceSidePanelCommand(
                    { session: store.get(sessionAtom), isExpanded: store.get(layoutAtom).isExpanded },
                    { type: SIDE_PANEL_CONTROL_COMMANDS.patchPayload, payload }
                );
                store.set(sessionAtom, transition.state.session);
                return transition.result;
            }),
        [controlQueue, store]
    );

    const setLayout = useCallback(
        (isExpanded: boolean) =>
            controlQueue.run(async () => {
                const transition = reduceSidePanelCommand(
                    { session: store.get(sessionAtom), isExpanded: store.get(layoutAtom).isExpanded },
                    { type: SIDE_PANEL_CONTROL_COMMANDS.setLayout, isExpanded }
                );
                store.set(layoutAtom, { isExpanded: transition.state.isExpanded });
                return transition.result;
            }),
        [controlQueue, store]
    );

    return useMemo(
        () => ({ open, close, toggle, setSubject, patchPayload, setLayout }),
        [close, open, patchPayload, setLayout, setSubject, toggle]
    );
}
